import { get } from '@common/Http';
import {
    defaultPageViewMap,
    GA_EVENT_PAGE_VIEW,
    GA_PRODUCT_PAGE,
    GA_LISTING_PAGE,
    GA_SEARCH_PAGE,
    GA_PROFILE_PAGE,
    GA_EVENT_DETAIL,
    SP_EVENT_DETAIL,
    SP_EVENT_PAGE_VIEW,
    GA4_EVENT_VIEW_ITEM
} from '@common/Tracking';
import { getBmpListValue } from '@common/Tracking/BmpProductView/trackList';
import { setRealListValue } from '@common/Tracking/RealList';
import { parseJSON } from '@helpers/data';
import { pushEventToDataLayer, ga4event } from '@helpers/dataLayer';
import { isFeatureActive } from '@helpers/index';
import { loadState, remove } from '@helpers/localStorage';
import { getVisitorDataCookieAsObject } from '@helpers/visitor';
import {
    KEVEL_ID, SEARCH_RESULT_PAGE_PRODUCTS, SEARCH_SUGGESTION_PRODUCT, SPONSORED_CLICK_URL
} from '../constants/localStorage';
import { formatData, getGaClientId, assembleGaPageViewData } from '../helpers/GaHelpers';
import { getItemsParameters } from '../itemsParameters';

// GA view_item
const GA4ViewItemEvent = () => {
    const eventData = parseJSON(App.Config.ga4ViewItemTrackingParams) ?? {};
    const itemsParameters = getItemsParameters() ?? {};

    if (!Array.isArray(eventData.items)) {
        eventData.items = [{}];
    }

    eventData.items[0] = {
        ...eventData.items[0],
        ...itemsParameters
    };

    ga4event(GA4_EVENT_VIEW_ITEM, eventData);
};

export const trackSponsoredCardClick = async () => {
    const sponsoredCardClickUrl = loadState(SPONSORED_CLICK_URL);

    if (sponsoredCardClickUrl) {
        try {
            await get(sponsoredCardClickUrl);
        } catch (error) {}
    }

    remove(SPONSORED_CLICK_URL);
};

/**
 * Send product detail view event to data layer.
 */
export const trackGaDetailView = () => {
    if (App.Config.Controller !== GA_PRODUCT_PAGE) {
        return;
    }

    const {
        page_path: pagePath,
        page_type: pageType
    } = App.Config.GaTrackingPage;

    const formatedPagePath = pagePath.replace(/([\w-]*\/[\w-]*)$/, 'product_detail');

    const {
        id,
        brand,
        category,
        name,
        variant,
        currencyCode,
        navigationId,
        has_sustainability_icon: hasSustainabilityIcon,
        has_icon: hasIcon,
        hasVideo360,
        variant_id: variantId,
        product_id: productId
    } = parseJSON(App.Config.productPageView);

    const { item_list_name: itemListName } = getItemsParameters() ?? {};

    setRealListValue(itemListName);

    const eventData = {
        ...formatData(getVisitorDataCookieAsObject()),
        ...App.Config.GaTrackingData,
        client_id: getGaClientId(),
        page_path: formatedPagePath,
        page_type: pageType,
        navigation_id: navigationId !== 0 ? navigationId : null,
        ecommerce: {
            currencyCode,
            detail: {
                actionField: {
                    list: itemListName
                },
                products: [{
                    category,
                    name,
                    variant,
                    id: id?.toUpperCase(),
                    brand: brand?.toUpperCase(),
                    has_sustainability_icon: hasSustainabilityIcon,
                    has_icon: hasIcon,
                    hasVideo360
                }]
            }
        }
    };

    pushEventToDataLayer(GA_EVENT_DETAIL, null, eventData);

    const isSponsoredTrackingFeatureActive = isFeatureActive('PLT-47472-send-impression-and-click-tracking-to-snow-plow');
    const isSponsoredClickUrlFeatureActive = isFeatureActive('PLT-64769-some-clicks-not-coming-through-for-sponsored-products');
    const kevelId = loadState(KEVEL_ID);
    const isSponsoredProduct = isSponsoredTrackingFeatureActive && kevelId;

    const bmpProductViewData = {
        category,
        list: !getBmpListValue() ? itemListName : getBmpListValue(),
        product_id: productId,
        variant_id: variantId
    };
    const sponsoredProductData = { ...bmpProductViewData, kevel_ad_id: kevelId };
    let dataToSend = null;
    let dataToRemoveFromLocal = null;

    const searchSuggestionsData = loadState(SEARCH_SUGGESTION_PRODUCT) || {};
    const searchResultPageData = loadState(SEARCH_RESULT_PAGE_PRODUCTS) || {};
    const isFromSearchSuggestions = Object.keys(searchSuggestionsData).length > 0;
    const isFromSearchResultPage = Object.keys(searchResultPageData).length > 0;

    if (isSponsoredProduct) {
        dataToSend = sponsoredProductData;
        dataToRemoveFromLocal = KEVEL_ID;
    } else if (isFromSearchSuggestions) {
        dataToSend = searchSuggestionsData;
        dataToRemoveFromLocal = SEARCH_SUGGESTION_PRODUCT;
    } else if (isFromSearchResultPage) {
        dataToSend = searchResultPageData;
        dataToRemoveFromLocal = SEARCH_RESULT_PAGE_PRODUCTS;
    } else {
        dataToSend = bmpProductViewData;
    }

    remove(dataToRemoveFromLocal);
    pushEventToDataLayer(SP_EVENT_DETAIL, null, dataToSend);

    if (isSponsoredClickUrlFeatureActive) {
        trackSponsoredCardClick();
    }

    if (isFeatureActive('PLT-44872_enable_ga4_view_item_tracking_event')) {
        GA4ViewItemEvent();
    }
};

const startPageviewTracking = (map, customAttributes) => {
    const eventData = assembleGaPageViewData(map, customAttributes);
    pushEventToDataLayer(GA_EVENT_PAGE_VIEW, null, eventData.gaEventData);
    pushEventToDataLayer(SP_EVENT_PAGE_VIEW, null, eventData.snowplowEventData);
};

export const pushGaPageView = (customAttributes = {}) => {
    startPageviewTracking(defaultPageViewMap, customAttributes);
};

/**
 * Send pageview event to data layer.
 */
export const trackGaPageView = (map = defaultPageViewMap) => {
    if ([GA_LISTING_PAGE, GA_SEARCH_PAGE, GA_PROFILE_PAGE].includes(App.Config.Controller)) {
        window.addEventListener(GA_EVENT_PAGE_VIEW, e => startPageviewTracking(map, e.detail));
    } else {
        startPageviewTracking(map);
    }
};

export const dispatchGaPageviewEvent = (detail = {}) => {
    const gaPageViewEvent = new CustomEvent(GA_EVENT_PAGE_VIEW, { detail });
    window.dispatchEvent(gaPageViewEvent);
};
